import { combineActions, handleActions } from 'redux-actions';
import { setupUndoForAction, completeWithError, resetResources } from 'data/qcsSyncResourceReducerHelpers';
import { deleteAccountSuccess, syncDeletedAccount } from 'data/accounts/actions';
import { isTransferTxn, findMatchingTransferTx } from 'data/transactions/utilsNoDependencies';
import { deleteScheduledTransaction, deleteScheduledTransactionSuccess, deleteScheduledTransactionFailure } from 'data/scheduledTransactions/scheduledTransactionsActions';

import { TransactionStore, CashFlowTransaction } from './types';
import * as actions from './actions';

import { upsertTxns, setupTxnsUndo } from './reducerHelpers';

const reducer = handleActions({

  // Called when triggering a CRUD operation on transactions. Sagas are also
  // expected to handle the action by initiating the CRUD operation wiht QCS.
  //
  [combineActions(
    actions.getTransactions,
    actions.performTransactionAction,
  )]: (state) => state.set('isLoading', true),

  [combineActions(
    actions.createTransactions,
    actions.deleteTransactions,
    actions.updateTransactions,
    actions.autoCategorizeTransactions,
    actions.updateTransactionCategoriesForPayeeAction,
  )]: (state) => state,

  [combineActions(
    deleteAccountSuccess,
    syncDeletedAccount    // SYNC_SIDE_EFFECT
  )]:(state, { asyncDispatch, payload: account }) => {
    const txns = state.transactionsByAccountId.get(account.id);
    if (txns) {
      const txnsToDelete = [];
      if (account.type === 'GOAL') {
        txns.forEach((txn) => {
          if (isTransferTxn(txn)) {
            const txnList = state.transactionsByAccountId.get(txn.coa.id);
            const xfrTxn = findMatchingTransferTx(txn, txnList).txn;
            txnsToDelete.push(xfrTxn.set('isDeleted', true));
          }
        });
        if (txnsToDelete.length > 0) {
          // must use optimistic to make the REDUX change stick (transactions reducer assumes optimistic updating)
          return upsertTxns(state, { resources: txnsToDelete });
          // asyncDispatch(actions.updateTransactionsSuccessOptimistic({ resources: txnsToDelete }));
        }
      } else {
        // for non goal accounts, we schedule a transactions GET to retrieve QCS changes
        // todo: BUMP SERVER
        setTimeout(() => asyncDispatch(actions.getTransactions()), 2000);
      }
      return state.set('transactionsByAccountId', state.transactionsByAccountId.remove(account.id));
    }
    return state;
  },

  [combineActions(
    actions.clearLastReduction,
  )]: (state) => state.set('lastReduction', null),

  // Called when a CRUD operation is successful on transactions. The success
  // action is triggered by the Sagas.
  [actions.getTransactionsSuccess]:
   (state, { payload: data }) =>
     upsertTxns(state.merge({ isLoading: false, error: undefined }), data),

  [actions.performTransactionActionSuccess]:
    (state, action) => {
      setupUndoForAction(action);
      return upsertTxns(state.set('isLoading', false), { resources: action.meta.response.data.resources.map((transaction) => new CashFlowTransaction(transaction))});
    },

  // This assumes the OPTIMISTIC update was used and succeeded
  // thus we just reverse the loading state
  [combineActions(
    actions.updateTransactionsSuccess,
    actions.deleteTransactionsSuccess
  )]: (state) =>
    state.set('isLoading', false),

  // These actions will provide an optimistic update to REDUX rather than
  // waiting for QCS
  [combineActions(
    actions.updateTransactionsSuccessOptimistic,
    actions.deleteTransactionsSuccessOptimistic,
    actions.createTransactionsSuccess,
  )]: (state, action) => {
    setupTxnsUndo(state, action);
    return upsertTxns(state, action.payload);
  },
  [actions.createTransactionsSuccessOptimistic]:
    (state, { payload: data }) =>
      upsertTxns(state, data),

  [actions.getTransactionsFailure]:
    (state, {payload: error}) =>
      completeWithError(state, error),

  [combineActions(
    actions.createTransactionsFailure,
    actions.updateTransactionsFailure,
    actions.deleteTransactionsFailure,
    actions.performTransactionActionFailure,
    deleteScheduledTransactionFailure,
  )]: (state, { payload: error, asyncDispatch }) => {
    asyncDispatch(actions.getTransactions());
    return resetResources(state, error);
  },

  [combineActions(
    deleteScheduledTransaction,
    deleteScheduledTransactionSuccess,
  )]: (state, {payload}) =>
    state.merge({
      transactionsByAccountId:
        state.transactionsByAccountId.map((transactions) => transactions.filter((transaction) => (
          transaction.stModelId !== payload.id
          || transaction.source !== 'SCHEDULED_TRANSACTION_PENDING'
          || transaction.state !== 'PENDING'
          || transaction.matchState !== 'NOT_MATCHED'
        ))),
    }),

}, new TransactionStore());

export const REDUCER_KEY = 'transactionStore';
export default reducer;
